import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { axiosInstance } from '../App';
import { X, Home, Package, Wrench, FileCheck, Calendar, PiggyBank, Users, Box, GitPullRequest, LogOut, Warehouse, Factory, ChevronDown, ChevronRight, ClipboardList, BarChart3, Bell, Edit3, DollarSign } from 'lucide-react';

const SideMenu = ({ user, isOpen, onClose, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAnimating, setIsAnimating] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [reminderCount, setReminderCount] = useState(0);
  const [hasReportAccess, setHasReportAccess] = useState(false);
  
  // Use effective_role for access control (supports custom roles with full permissions)
  const effectiveRole = user?.effective_role || user?.role;

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  // Check report access
  useEffect(() => {
    const checkReportAccess = async () => {
      if (!user) {
        console.log('[SideMenu] No user, skipping report access check');
        return;
      }
      
      // Use effective_role for access control (supports custom roles with full access)
      const effectiveRole = user.effective_role || user.role;
      console.log('[SideMenu] Checking report access for user:', user.username, 'role:', user.role, 'effective_role:', effectiveRole);
      
      // Admin and Manager always have access
      if (effectiveRole === 'Admin' || effectiveRole === 'Manager') {
        console.log('[SideMenu] Admin/Manager detected, granting access');
        setHasReportAccess(true);
        return;
      }
      
      // Check for staff users
      try {
        const response = await axiosInstance.get('/auth/check-report-access');
        console.log('[SideMenu] Report access check response:', response.data);
        setHasReportAccess(response.data.has_access);
      } catch (error) {
        console.error('[SideMenu] Error checking report access:', error);
        setHasReportAccess(false);
      }
    };

    checkReportAccess();
    
    // Recheck every minute to handle expiry
    const interval = setInterval(checkReportAccess, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
  };

  const toggleSubmenu = (itemPath) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [itemPath]: !prev[itemPath]
    }));
  };

  // Filter menu items based on report access
  const getFilteredMenuItems = () => {
    const baseMenuItems = [
      { path: '/', icon: Home, label: 'Dashboard', testId: 'menu-dashboard' },
      { 
        path: '/orders', 
        icon: Package, 
        label: 'Orders', 
        testId: 'menu-orders',
        subItems: [
          { path: '/orders/create', label: 'New Order' },
          { path: '/bookings/create', label: 'New Booking' },
          ...(hasReportAccess ? [{ path: '/orders/reports', label: 'Reports' }] : [])
        ]
      },
      { path: '/selections', icon: Edit3, label: 'Selections', testId: 'menu-selections' },
      { path: '/submissions/my-all', icon: ClipboardList, label: 'My Submissions', testId: 'menu-my-submissions' },
      { path: '/payment-reminders', icon: Bell, label: 'Payment Reminders', testId: 'menu-payment-reminders', badge: reminderCount },
      { 
        path: '/approvals/create', 
        icon: FileCheck, 
        label: 'Approvals', 
        testId: 'menu-approvals',
        subItems: [
          { path: '/approvals/create', label: 'New Approval' },
          ...(hasReportAccess ? [{ path: '/approvals/reports', label: 'Reports' }] : [])
        ]
      },
      { 
        path: '/kitty/enrollments/create', 
        icon: PiggyBank, 
        label: 'Kitty', 
        testId: 'menu-kitty',
        subItems: [
          { path: '/kitty/enrollments/create', label: 'New Enrollment' },
          ...(hasReportAccess ? [{ path: '/kitty', label: 'Enrollments & Reports' }] : [{ path: '/kitty/enrollments', label: 'Enrollments' }]),
          { path: '/kitty/schemes', label: 'Schemes' }
        ]
      },
    ];

    // Only add Reports menu if user has access
    if (hasReportAccess) {
      console.log('[SideMenu] Adding Reports menu - user has access');
      baseMenuItems.push({
        path: '/reports', 
        icon: BarChart3, 
        label: 'Reports', 
        testId: 'menu-reports',
        subItems: [
          { path: '/reports/payment-ledger', label: 'Payment Ledger' },
          { path: '/reports/item-tracker', label: 'Item Tracker' }
        ]
      });
    } else {
      console.log('[SideMenu] NOT adding Reports menu - no access');
    }

    console.log('[SideMenu] Final menu items count:', baseMenuItems.length, 'hasReportAccess:', hasReportAccess);
    return baseMenuItems;
  };

  const menuItems = getFilteredMenuItems();

  // Fetch pending approvals count for admin
  useEffect(() => {
    const effectiveRole = user?.effective_role || user?.role;
    if (effectiveRole === 'Admin' || effectiveRole === 'Manager') {
      const fetchPendingCount = async () => {
        try {
          const { axiosInstance } = await import('../App');
          const response = await axiosInstance.get('/admin/pending-approvals');
          setPendingCount(response.data.total_count || 0);
        } catch (error) {
          console.error('Error fetching pending count:', error);
        }
      };
      fetchPendingCount();
      // Refresh every 30 seconds
      const interval = setInterval(fetchPendingCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Fetch reminder count (today + overdue)
  useEffect(() => {
    const fetchReminderCount = async () => {
      try {
        // Only fetch if user is logged in
        if (!user) return;
        
        const { axiosInstance } = await import('../App');
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch bookings with reminders
        const bookingsRes = await axiosInstance.get('/bookings');
        const bookingReminders = bookingsRes.data.filter(b => 
          b.reminder_date && b.reminder_date <= today && b.status !== 'Converted'
        );
        
        // Fetch orders with reminders
        const ordersRes = await axiosInstance.get('/orders');
        const orderReminders = ordersRes.data.filter(o => 
          o.reminder_date && o.reminder_date <= today
        );
        
        setReminderCount(bookingReminders.length + orderReminders.length);
      } catch (error) {
        console.error('Error fetching reminder count:', error);
        setReminderCount(0);
      }
    };
    
    // Fetch when user is available
    if (user) {
      fetchReminderCount();
      
      // Refresh every 60 seconds
      const interval = setInterval(fetchReminderCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const adminItems = [
    { path: '/customers', icon: Users, label: 'Customers', testId: 'menu-customers' },
    { path: '/admin/approvals', icon: GitPullRequest, label: 'Pending Approvals', testId: 'menu-admin-approvals', badge: pendingCount },
    { path: '/admin/settings', icon: Wrench, label: 'Settings', testId: 'menu-settings' },
  ];

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ease-out ${
          isAnimating ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleClose}
        data-testid="menu-backdrop"
      />

      {/* Side Menu */}
      <div
        className={`fixed top-0 left-0 h-full w-80 bg-gradient-to-br from-white via-amber-50 to-orange-50 shadow-2xl z-50 transform transition-transform duration-300 ease-out ${
          isAnimating ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="side-menu"
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-6 bg-gradient-to-r from-amber-100 to-orange-100 border-b-2 border-amber-200">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">{(user?.full_name || user?.name || 'U').charAt(0)}</span>
                </div>
                <div>
                  <h2 className="font-bold text-gray-800">{user?.full_name || user?.name || 'User'}</h2>
                  <p className="text-sm text-gray-600">{user?.role || 'User'}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-white rounded-full transition-all"
                data-testid="close-menu"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="text-xs text-amber-700 font-medium bg-white px-3 py-1 rounded-full inline-block">
              Jewelry Operations Suite
            </div>
          </div>

          {/* Menu Items */}
          <div className="flex-1 overflow-y-auto py-4 px-3">
            <nav className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || (item.subItems && item.subItems.some(sub => location.pathname === sub.path));
                const isSubmenuOpen = openSubmenus[item.path] || isActive;
                return (
                  <div key={item.path}>
                    <button
                      onClick={() => item.subItems ? toggleSubmenu(item.path) : handleNavigate(item.path)}
                      className={`w-full flex items-center justify-between gap-4 px-4 py-3 rounded-xl transition-all ${
                        isActive
                          ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-lg transform scale-105'
                          : 'text-gray-700 hover:bg-white hover:shadow-md'
                      }`}
                      data-testid={item.testId}
                    >
                      <div className="flex items-center gap-4">
                        <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-amber-600'}`} />
                        <span className="font-semibold">{item.label}</span>
                      </div>
                      {item.subItems && (
                        isSubmenuOpen ? 
                          <ChevronDown className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} /> :
                          <ChevronRight className={`w-4 h-4 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                      )}
                    </button>
                    {item.subItems && isSubmenuOpen && (
                      <div className="ml-8 mt-1 space-y-1">
                        {item.subItems.map((subItem) => (
                          <button
                            key={subItem.path}
                            onClick={() => handleNavigate(subItem.path)}
                            className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-all ${
                              location.pathname === subItem.path
                                ? 'bg-amber-100 text-amber-800 font-semibold'
                                : 'text-gray-600 hover:bg-amber-50'
                            }`}
                          >
                            {subItem.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>

            {/* Admin Section */}
            {(effectiveRole === 'Admin' || effectiveRole === 'Controller') && (
              <div className="mt-6">
                <div className="px-4 mb-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Management</p>
                </div>
                <nav className="space-y-1">
                  {adminItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.path;
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        className={`w-full flex items-center justify-between gap-4 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-white shadow-lg transform scale-105'
                            : 'text-gray-700 hover:bg-white hover:shadow-md'
                        }`}
                        data-testid={item.testId}
                      >
                        <div className="flex items-center gap-4">
                          <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-amber-600'}`} />
                          <span className="font-semibold">{item.label}</span>
                        </div>
                        {item.badge > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[24px] text-center">
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}

          </div>

          {/* Footer */}
          <div className="p-4 border-t-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
            <button
              onClick={() => {
                onLogout();
                handleClose();
              }}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 transition-all"
              data-testid="menu-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-semibold">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default SideMenu;
